import React, { FC, useState, useEffect } from 'react';
import './App.scss';
import PartyCreation from './components/PartyCreation';
import PartyJoining from './components/PartyJoining';
import VideoRoom from './components/VideoRoom';
import HasurGate from './components/HasurGate';
import type { AppMode, PartyData } from './types';
import { API_BASE_URL } from './config';

// Matches a valid 8-char hex party ID at the root path, e.g. /abc12345
const PARTY_PATH_RE = /^\/([a-f0-9]{8})$/i;

// Fixed ID for the permanent hasur party
const HASUR_PARTY_ID = 'hasurroom';

interface CheckResponse {
  valid: boolean;
  partyId?: string;
  partyName?: string;
  error?: string;
}

const App: FC = () => {
  const [mode, setMode] = useState<AppMode>('home');
  const [partyData, setPartyData] = useState<PartyData | null>(null);
  const [username, setUsername] = useState<string>('');
  const [initialJoinPartyId, setInitialJoinPartyId] = useState<string>('');
  const [hasurToken, setHasurToken] = useState<string>('');

  // ── On mount: check URL ───────────────────────────────────────────────────
  useEffect(() => {
    // Secret /hasur route
    if (window.location.pathname === '/hasur') {
      setMode('hasur');
      return;
    }

    // Regular party deep-link
    const match = window.location.pathname.match(PARTY_PATH_RE);
    if (!match) return;
    const partyId = match[1].toLowerCase();
    fetch(`${API_BASE_URL}/api/party/${partyId}/check`)
      .then<CheckResponse>((r) => r.json())
      .then((data) => {
        if (data.valid) {
          setInitialJoinPartyId(partyId);
          setMode('join');
        } else {
          window.history.replaceState({}, '', '/');
        }
      })
      .catch(() => {
        window.history.replaceState({}, '', '/');
      });
  }, []);

  // ── Handle browser back/forward ───────────────────────────────────────────
  useEffect(() => {
    const onPopState = (): void => {
      const path = window.location.pathname;
      if (path === '/hasur') {
        setMode('hasur');
        setPartyData(null);
        setUsername('');
        return;
      }
      if (!path.match(PARTY_PATH_RE)) {
        setMode('home');
        setPartyData(null);
        setUsername('');
        setInitialJoinPartyId('');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePartyCreated = (data: PartyData): void => {
    setPartyData(data);
    setUsername(data.hostUsername);
    setMode('host');
    window.history.pushState({}, '', `/${data.partyId}`);
  };

  const handleJoined = (data: PartyData): void => {
    setPartyData(data);
    setUsername(data.username ?? '');
    setMode('guest');
    window.history.pushState({}, '', `/${data.partyId}`);
  };

  const handleLeave = (): void => {
    setMode('home');
    setPartyData(null);
    setUsername('');
    setInitialJoinPartyId('');
    window.history.pushState({}, '', '/');
  };

  // Called when HasurGate auth+quiz succeeds — token lives in memory only
  const handleHasurEnter = (enteredUsername: string, isHost: boolean, token: string): void => {
    setUsername(enteredUsername);
    setHasurToken(token);
    setPartyData({
      partyId: HASUR_PARTY_ID,
      partyName: 'Secret Party 🔐',
      hostUsername: isHost ? enteredUsername : '',
      username: enteredUsername,
    });
    setMode(isHost ? 'host' : 'guest');
    // Keep URL at /hasur — no pushState change
  };

  return (
    <div className="App">
      {mode === 'home' && (
        <div className="home-container">
          <h1 className="app-title">Sync Show</h1>
          <p className="app-subtitle">Watch videos together in sync</p>
          <div className="button-group">
            <button className="btn btn-primary" onClick={() => setMode('create')}>
              Create Party
            </button>
            <button className="btn btn-secondary" onClick={() => setMode('join')}>
              Join Party
            </button>
          </div>
        </div>
      )}

      {mode === 'create' && (
        <PartyCreation
          onPartyCreated={handlePartyCreated}
          onBack={() => setMode('home')}
        />
      )}

      {mode === 'join' && (
        <PartyJoining
          onJoined={handleJoined}
          onBack={() => {
            setInitialJoinPartyId('');
            window.history.replaceState({}, '', '/');
            setMode('home');
          }}
          initialPartyId={initialJoinPartyId}
        />
      )}

      {mode === 'hasur' && (
        <HasurGate onEnter={handleHasurEnter} />
      )}

      {(mode === 'host' || mode === 'guest') && partyData && (
        <VideoRoom
          partyId={partyData.partyId}
          username={username}
          isHost={mode === 'host'}
          partyName={partyData.partyName}
          onLeave={handleLeave}
          joinToken={hasurToken || undefined}
        />
      )}
    </div>
  );
};

export default App;
